import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "~/shared/utils/i18n";
import { Link } from "@remix-run/react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaClock,
  FaInfoCircle,
  FaRegClock,
  FaExclamationTriangle,
  FaCheckCircle,
  FaCalendarAlt,
  FaGift,
  FaGraduationCap,
  FaMoneyBillWave,
  FaShieldAlt,
  FaHome,
  FaStar,
  FaCrown,
  FaFlag,
  FaMosque,
  FaPrayingHands,
  FaPalette,
  FaRocket,
  FaHeart,
  FaTimes,
  FaSpinner
} from "react-icons/fa";
import "~/shared/components/dark-mode.css";
import "~/shared/components/animations.css";
import "~/shared/components/performance.css";
import "~/features/dates/saudi-dates.css";
import { saudiDates, validateSaudiDates, testAutoUpdate, getFutureMonthlyDates, gregorianToHijri, testIslamicCountdown, debugIslamicCountdown, testIslamicCardsVisibility, comprehensiveIslamicTest, fixIslamicDates, testUpdatedIslamicDates, applyIslamicFixes, test2025IslamicDates } from "~/shared/utils/saudi-dates-data";



// أنواع البيانات للكاش
interface TimeRemainingData {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  hasPassed: boolean;
  isToday: boolean;
  progress: number;
}

interface CacheEntry {
  data: TimeRemainingData;
  timestamp: number;
}

// أنواع البيانات للشبكة والذاكرة
interface NetworkInformation {
  effectiveType?: string;
}

interface PerformanceMemory {
  jsHeapSizeLimit?: number;
}

declare global {
  interface Navigator {
    connection?: NetworkInformation;
    mozConnection?: NetworkInformation;
    webkitConnection?: NetworkInformation;
    hardwareConcurrency?: number;
  }
  
  interface Performance {
    memory?: PerformanceMemory;
  }
}

// تحقق من صحة المواعيد عند التشغيل (للمطور) - محسن للأداء
if (typeof window !== 'undefined') {
  // تأخير التحقق لتحسين وقت التحميل الأولي
  setTimeout(() => {
    const errors = validateSaudiDates(saudiDates);
    if (errors.length > 0) {
      // eslint-disable-next-line no-console
      console.warn("Saudi Dates Validation Errors:", errors);
    }
    
    // اختبار التحديث التلقائي للمواعيد (للمطور)
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log("Testing auto-update for all dates:");
      testAutoUpdate();
      
      // اختبار دقة المواعيد الشهرية
      // eslint-disable-next-line no-console
      console.log("Testing monthly dates accuracy:");
      import("~/shared/utils/saudi-dates-data").then(({ testMonthlyDates }) => {
        testMonthlyDates();
      });
      
      // اختبار عداد المواعيد الإسلامية
      // eslint-disable-next-line no-console
      console.log("Testing Islamic dates countdown:");
      testIslamicCountdown();
      
      // فحص تفصيلي لعداد المواعيد الإسلامية
      // eslint-disable-next-line no-console
      console.log("Debugging Islamic dates countdown:");
      debugIslamicCountdown();
      
      // تشخيص التواريخ الإسلامية
      // eslint-disable-next-line no-console
      console.log("Debugging Islamic dates:");
      debugIslamicDates();
      
      // اختبار ظهور البطاقات الإسلامية
      // eslint-disable-next-line no-console
      console.log("Testing Islamic cards visibility:");
      testIslamicCardsVisibility();
      
      // اختبار شامل ودقيق للمواعيد الإسلامية
      // eslint-disable-next-line no-console
      console.log("Comprehensive Islamic test:");
      comprehensiveIslamicTest();
      
      // تطبيق الإصلاح الشامل للمواعيد الإسلامية
      // eslint-disable-next-line no-console
      console.log("Applying comprehensive Islamic dates fix:");
      fixIslamicDates();
      
      // اختبار المواعيد الإسلامية المحدثة
      // eslint-disable-next-line no-console
      console.log("Testing updated Islamic dates:");
      testUpdatedIslamicDates();
      
      // تطبيق الإصلاحات في الوقت الفعلي
      // eslint-disable-next-line no-console
      console.log("Applying Islamic fixes in real-time:");
      applyIslamicFixes();
    }
  }, 1000);
}

// ترجمات التطبيق - محسنة مع الكاش
const translations = {
  ar: {
    title: "مواعيد السعودية",
    subtitle: "الوقت المتبقي للتواريخ المهمة في المملكة",
    backToHome: "العودة للرئيسية",
    timeRemaining: "الوقت المتبقي",
    days: "أيام",
    hours: "ساعات",
    minutes: "دقائق",
    seconds: "ثواني",
    hasPassed: "مر",
    isToday: "اليوم",
    loading: "جاري التحميل...",
    error: "حدث خطأ في التحميل",
    retry: "إعادة المحاولة",
    performanceMode: "وضع الأداء المحسن",
    categories: {
      salaries: "الرواتب",
      support: "الدعم",
      education: "التعليم",
      other: "أخرى",
      holidays: "المناسبات",
      services: "الخدمات"
    },
    dates: {
      governmentSalary: {
        title: "رواتب القطاع الحكومي",
        description: "رواتب موظفي القطاع الحكومي"
      },
      retiredSalary: {
        title: "رواتب المتقاعدين",
        description: "رواتب المتقاعدين"
      },
      housingSupport: {
        title: "الدعم السكني",
        description: "دفعات الدعم السكني"
      },
      socialSecurity: {
        title: "الضمان الاجتماعي",
        description: "مخصصات الضمان الاجتماعي"
      },
      academicYear: {
        title: "بداية العام الدراسي",
        description: "بداية العام الدراسي الجديد"
      },
      eidAlFitr: {
        title: "عيد الفطر",
        description: "عيد الفطر المبارك"
      },
      eidAlAdha: {
        title: "عيد الأضحى",
        description: "عيد الأضحى المبارك"
      },
      nationalDay: {
        title: "اليوم الوطني",
        description: "اليوم الوطني السعودي"
      },
      foundationDay: {
        title: "يوم التأسيس",
        description: "يوم التأسيس السعودي"
      },
      ramadan: {
        title: "شهر رمضان",
        description: "شهر رمضان المبارك"
      },
      hajj: {
        title: "الحج",
        description: "موسم الحج"
      },
      citizenAccount: {
        title: "إيداع حساب المواطن",
        description: "إيداع حساب المواطن"
      }
    },
    lastUpdated: "آخر تحديث",
    refresh: "تحديث",
    settings: "الإعدادات",
    notifications: "الإشعارات",
    about: "حول التطبيق",
    progress: "التقدم",
    completed: "مكتمل",
    remaining: "متبقي",
    showCompleted: "إظهار المكتمل",
    hideCompleted: "إخفاء المكتمل",
    actualDate: "الموعد الفعلي"
  },
  en: {
    title: "Saudi Dates",
    subtitle: "Time remaining for important dates in the Kingdom",
    backToHome: "Back to Home",
    timeRemaining: "Time Remaining",
    days: "days",
    hours: "hours",
    minutes: "minutes",
    seconds: "seconds",
    hasPassed: "passed",
    isToday: "Today",
    loading: "Loading...",
    error: "Loading error occurred",
    retry: "Retry",
    performanceMode: "Performance Mode",
    categories: {
      salaries: "Salaries",
      support: "Support",
      education: "Education",
      other: "Other",
      holidays: "Holidays",
      services: "Services"
    },
    dates: {
      governmentSalary: {
        title: "Government Sector Salaries",
        description: "Government employees salaries"
      },
      retiredSalary: {
        title: "Retirees Salaries",
        description: "Retirees salaries"
      },
      housingSupport: {
        title: "Housing Support",
        description: "Housing support payments"
      },
      socialSecurity: {
        title: "Social Security",
        description: "Social security benefits"
      },
      academicYear: {
        title: "Academic Year Start",
        description: "New academic year start"
      },
      eidAlFitr: {
        title: "Eid Al-Fitr",
        description: "Eid Al-Fitr holiday"
      },
      eidAlAdha: {
        title: "Eid Al-Adha",
        description: "Eid Al-Adha holiday"
      },
      nationalDay: {
        title: "National Day",
        description: "Saudi National Day"
      },
      foundationDay: {
        title: "Foundation Day",
        description: "Saudi Foundation Day"
      },
      ramadan: {
        title: "Ramadan",
        description: "Holy month of Ramadan"
      },
      hajj: {
        title: "Hajj",
        description: "Hajj season"
      },
      citizenAccount: {
        title: "Citizen Account Deposit",
        description: "Citizen Account deposit"
      }
    },
    lastUpdated: "Last Updated",
    refresh: "Refresh",
    settings: "Settings",
    notifications: "Notifications",
    about: "About",
    progress: "Progress",
    completed: "Completed",
    remaining: "Remaining",
    showCompleted: "Show Completed",
    hideCompleted: "Hide Completed",
    actualDate: "Actual Date"
  }
};

// دالة محسنة لحساب الوقت المتبقي مع كاش
const timeRemainingCache = new Map<string, CacheEntry>();
const CACHE_DURATION = 1000; // ثانية واحدة

function calculateTimeRemaining(targetDate: Date): TimeRemainingData {
  const now = Date.now();
  const cacheKey = targetDate.getTime().toString();
  const cached = timeRemainingCache.get(cacheKey);
  
  // استخدام الكاش إذا كان صالحاً
  if (cached && (now - cached.timestamp) < CACHE_DURATION) {
    return cached.data;
  }
  
  const difference = targetDate.getTime() - now;
  
  if (difference <= 0) {
    const result: TimeRemainingData = {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      hasPassed: true,
      isToday: false,
      progress: 100
    };
    
    // حفظ في الكاش
    timeRemainingCache.set(cacheKey, { data: result, timestamp: now });
    return result;
  }
  
  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);
  
  // حساب التقدم (تقريبي)
  const totalSeconds = difference / 1000;
  const progress = Math.max(0, Math.min(100, (totalSeconds / (30 * 24 * 60 * 60)) * 100)); // 30 يوم كحد أقصى
  
  const result: TimeRemainingData = {
    days,
    hours,
    minutes,
    seconds,
    hasPassed: false,
    isToday: days === 0 && hours < 24,
    progress: 100 - progress
  };
  
  // حفظ في الكاش
  timeRemainingCache.set(cacheKey, { data: result, timestamp: now });
  return result;
}

// مكون شريط التقدم محسن
function ProgressBar({ progress, color, reducedAnimations = false }: { 
  progress: number; 
  color: string;
  reducedAnimations?: boolean;
}) {
  return (
    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
      <motion.div
        className={`h-2 rounded-full ${color}`}
        initial={{ width: 0 }}
        animate={{ width: `${progress}%` }}
        transition={{ 
          duration: reducedAnimations ? 0.2 : 1, 
          ease: "easeOut" 
        }}
      />
    </div>
  );
}

// مكون تحميل محسن
function LoadingSpinner({ size = "md" }: { size?: "sm" | "md" | "lg" }) {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-6 h-6", 
    lg: "w-8 h-8"
  };
  
  return (
    <div className="flex items-center justify-center">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        className={`${sizeClasses[size]} text-blue-500`}
      >
        <FaSpinner />
      </motion.div>
    </div>
  );
}

// مكون خطأ محسن
function ErrorBoundary({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="text-center py-8">
      <div className="text-red-500 mb-4">
        <FaExclamationTriangle className="w-12 h-12 mx-auto" />
      </div>
      <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
      >
        إعادة المحاولة
      </button>
    </div>
  );
}

// مكون عرض التاريخ محسن للأداء
function DateCard({ 
  date, 
  lang, 
  showCompleted,
  onCardClick,
  performanceMode = 'MEDIUM_DEVICE'
}: { 
  date: typeof saudiDates[0], 
  lang: 'ar' | 'en',
  showCompleted: boolean,
  onCardClick: (date: typeof saudiDates[0]) => void,
  performanceMode?: 'LOW_END_DEVICE' | 'MEDIUM_DEVICE' | 'HIGH_END_DEVICE'
}) {
  const [timeRemaining, setTimeRemaining] = useState(calculateTimeRemaining(date.getNextDate()));
  const t = translations[lang as keyof typeof translations];
  const IconComponent = date.icon;
  
  // البطاقات المحددة التي تدعم الضغط
  const clickableCards = ['governmentSalary', 'retiredSalary', 'housingSupport', 'socialSecurity', 'citizenAccount'];
  const isClickable = clickableCards.includes(date.id);
  
  // تحسين الأداء: استخدام useMemo للقيم المحسوبة
  const isReducedAnimations = useMemo(() => performanceMode === 'LOW_END_DEVICE', [performanceMode]);
  
  // تحسين الأداء: استخدام useCallback للدوال
  const updateTimeRemaining = useCallback(() => {
    setTimeRemaining(calculateTimeRemaining(date.getNextDate()));
  }, [date]);
  

  
  const handleCardClick = useCallback(() => {
    if (isClickable) {
      onCardClick(date);
    }
  }, [isClickable, onCardClick, date]);
  
  useEffect(() => {
    // تحديد فاصل التحديث بناءً على نوع الجهاز
    const updateInterval = performanceMode === 'LOW_END_DEVICE' ? 5000 : 
                          performanceMode === 'MEDIUM_DEVICE' ? 2000 : 1000;
    
    const timer = setInterval(updateTimeRemaining, updateInterval);
    
    return () => clearInterval(timer);
  }, [updateTimeRemaining, performanceMode]);
  
  // إخفاء البطاقات المكتملة إذا لم يتم تفعيل إظهارها
  if (timeRemaining.hasPassed && !showCompleted) {
    return null;
  }
  
  const getStatusIcon = () => {
    if (timeRemaining.hasPassed) {
      return <FaCheckCircle className="w-5 h-5 text-green-500" />;
    }
    if (timeRemaining.isToday) {
      return <FaExclamationTriangle className="w-5 h-5 text-yellow-500" />;
    }
    return <FaRegClock className="w-5 h-5 text-blue-500" />;
  };
  
  const getStatusText = () => {
    if (timeRemaining.hasPassed) {
      return t.hasPassed;
    }
    if (timeRemaining.isToday) {
      return t.isToday;
    }
    return t.timeRemaining;
  };
  
  const getProgressColor = () => {
    if (timeRemaining.hasPassed) return 'bg-green-500';
    if (timeRemaining.isToday) return 'bg-yellow-500';
    if (timeRemaining.days <= 7) return 'bg-red-500';
    if (timeRemaining.days <= 14) return 'bg-orange-500';
    return 'bg-blue-500';
  };
  
  // تحسين الأداء: تقليل التأثيرات الحركية للأجهزة الضعيفة
  const animationProps = isReducedAnimations ? {
    initial: { opacity: 1 },
    animate: { opacity: 1 },
    transition: { duration: 0.1 }
  } : {
    initial: { opacity: 0, y: 20, scale: 0.95 },
    animate: { opacity: 1, y: 0, scale: 1 },
    transition: { duration: 0.5 }
  };
  
  const hoverProps = isReducedAnimations ? {} : {
    whileHover: { 
      scale: isClickable ? 1.02 : 1.01,
      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
    }
  };
  
  return (
    <motion.div
      {...animationProps}
      {...hoverProps}
      onClick={handleCardClick}
      className={`
        ${date.bgColor} rounded-2xl shadow-lg hover:shadow-xl
        border-2 ${date.borderColor}
        p-4 sm:p-6 transition-all duration-300
        ${timeRemaining.isToday ? 'ring-4 ring-yellow-400 ring-opacity-50' : ''}
        ${timeRemaining.hasPassed ? 'opacity-75' : ''}
        backdrop-blur-sm
        ${timeRemaining.hasPassed ? 'completed-card' : ''}
        ${isClickable ? 'cursor-pointer hover:scale-105 active:scale-95' : ''}
        ${isClickable ? 'relative overflow-hidden' : ''}
      `}
    >
      {/* تأثير النقر للبطاقات القابلة للضغط - محسن للأداء */}
      {isClickable && !isReducedAnimations && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 opacity-0 hover:opacity-100 transition-opacity duration-300"
          whileHover={{ scale: 1.05 }}
        />
      )}
      
      {/* مؤشر النقر للبطاقات القابلة للضغط - محسن للأداء */}
      {isClickable && (
        <div className="absolute top-3 right-3 rtl:top-3 rtl:left-3">
          <motion.div
            animate={isReducedAnimations ? {} : { scale: [1, 1.2, 1] }}
            transition={isReducedAnimations ? {} : { duration: 2, repeat: Infinity }}
            className="w-2 h-2 bg-purple-500 rounded-full shadow-lg"
          />
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-start justify-between mb-4 sm:mb-6 relative z-10">
        <div className="flex items-center space-x-3 sm:space-x-4 rtl:space-x-reverse">
          <div className={`
            p-3 sm:p-4 rounded-xl bg-gradient-to-br ${date.color} ${date.darkColor}
            text-white shadow-lg transform rotate-3 hover:rotate-0 transition-transform duration-300
          `}>
            <IconComponent className="w-5 h-5 sm:w-7 sm:h-7" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-xl font-bold text-gray-900 dark:text-white mb-1 truncate">
              {date.title}
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
              {date.description}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end">
          {getStatusIcon()}
          <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {getStatusText()}
          </span>
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="mb-4 sm:mb-6 relative z-10">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
            {t.progress}
          </span>
          <span className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
            {Math.round(timeRemaining.progress)}%
          </span>
        </div>
        <ProgressBar 
          progress={timeRemaining.progress} 
          color={getProgressColor()} 
          reducedAnimations={isReducedAnimations}
        />
      </div>
      
      {/* Time Remaining */}
      {!timeRemaining.hasPassed && (
        <div className="mb-4 sm:mb-6 relative z-10">
          <div className="grid grid-cols-4 gap-2 sm:gap-3">
            <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-2 sm:p-3 shadow-sm">
              <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                {timeRemaining.days}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {t.days}
              </div>
            </div>
            <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-2 sm:p-3 shadow-sm">
              <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                {timeRemaining.hours}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {t.hours}
              </div>
            </div>
            <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-2 sm:p-3 shadow-sm">
              <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                {timeRemaining.minutes}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {t.minutes}
              </div>
            </div>
            <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-2 sm:p-3 shadow-sm">
              <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                {timeRemaining.seconds}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {t.seconds}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Completed Status */}
      {timeRemaining.hasPassed && (
        <div className="text-center py-4 sm:py-6 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-700 relative z-10">
          <FaCheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-green-500 mx-auto mb-2 sm:mb-3" />
          <p className="text-green-600 dark:text-green-400 font-medium text-base sm:text-lg">
            {t.completed}
          </p>
        </div>
      )}
      
      {/* Footer */}
      <div className="flex flex-col space-y-3 sm:space-y-4 relative z-10">
        {/* الموعد الفعلي مع تأثير آخر تحديث - محسن للأداء */}
        <motion.div
          initial={isReducedAnimations ? { opacity: 1 } : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: isReducedAnimations ? 0.1 : 0.5, delay: isReducedAnimations ? 0 : 0.2 }}
          className="enhanced-actual-date-card"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <motion.div
                animate={isReducedAnimations ? {} : { rotate: 360 }}
                transition={isReducedAnimations ? {} : { duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-2.5 h-2.5 bg-gradient-to-r from-green-400 to-blue-500 rounded-full shadow-sm enhanced-dot"
              />
              <FaCalendarAlt className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 enhanced-date-icon" />
              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-semibold">
                {t.actualDate}:
              </span>
            </div>
            <div className="flex flex-col items-end">
              <DateDisplay 
                date={date.getNextDate()} 
                lang={lang} 
                dateId={date.id} 
              />
              <div className="flex items-center space-x-1 rtl:space-x-reverse mt-1">
                <div className="w-1 h-1 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {lang === 'ar' ? 'محدث' : 'Updated'}
                </span>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* الفئة */}
        <motion.div
          initial={isReducedAnimations ? { opacity: 1 } : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: isReducedAnimations ? 0.1 : 0.5, delay: isReducedAnimations ? 0 : 0.3 }}
          className="flex justify-between items-center"
        >
          <span className="enhanced-category-badge">
            {t.categories[date.category as keyof typeof t.categories]}
          </span>
          {isClickable && (
            <motion.div
              animate={isReducedAnimations ? {} : { x: [0, 5, 0] }}
              transition={isReducedAnimations ? {} : { duration: 2, repeat: Infinity }}
              className="text-purple-500 text-xs font-medium"
            >
              {lang === 'ar' ? 'اضغط للتفاصيل' : 'Click for details'}
            </motion.div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}

// مكون التصفية
function FilterButtons({ activeCategory, onCategoryChange, lang }: {
  activeCategory: string;
  onCategoryChange: (category: string) => void;
  lang: 'ar' | 'en';
}) {
  const t = translations[lang as keyof typeof translations];
  const categories = [
    { id: 'all', label: lang === 'ar' ? 'الكل' : 'All', icon: FaCalendarAlt },
    { id: 'salaries', label: t.categories.salaries, icon: FaMoneyBillWave },
    { id: 'support', label: t.categories.support, icon: FaShieldAlt },
    { id: 'education', label: t.categories.education, icon: FaGraduationCap },
    { id: 'holidays', label: t.categories.holidays, icon: FaGift },
    { id: 'services', label: t.categories.services, icon: FaHome },
    { id: 'other', label: t.categories.other, icon: FaStar }
  ];
  
  return (
    <div className="flex flex-wrap gap-2 sm:gap-3 mb-6 sm:mb-8 overflow-x-auto pb-2 saudi-scrollbar">
      {categories.map((category) => {
        const IconComponent = category.icon;
        return (
          <motion.button
            key={category.id}
            onClick={() => onCategoryChange(category.id)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`
              saudi-filter-btn flex items-center space-x-2 rtl:space-x-reverse px-3 sm:px-4 py-2 sm:py-3 rounded-xl text-xs sm:text-sm font-medium 
              transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap
              ${activeCategory === category.id ? 'active' : ''}
            `}
          >
            <IconComponent className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>{category.label}</span>
          </motion.button>
        );
      })}
    </div>
  );
}

// مكون الإحصائيات
function Statistics({ dates, lang }: { dates: typeof saudiDates, lang: 'ar' | 'en' }) {
  const t = translations[lang as keyof typeof translations];
  
  const stats = dates.reduce((acc, date) => {
    const timeRemaining = calculateTimeRemaining(date.getNextDate());
    if (timeRemaining.hasPassed) acc.completed++;
    else if (timeRemaining.isToday) acc.today++;
    else acc.upcoming++;
    return acc;
  }, { completed: 0, today: 0, upcoming: 0 });
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.5 }}
      className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8"
    >
      <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-2xl p-4 sm:p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-green-100 text-sm sm:text-base">{t.completed}</p>
            <p className="text-2xl sm:text-3xl font-bold">{stats.completed}</p>
          </div>
          <FaCheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-200" />
        </div>
      </div>
      
      <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white rounded-2xl p-4 sm:p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-yellow-100 text-sm sm:text-base">{t.isToday}</p>
            <p className="text-2xl sm:text-3xl font-bold">{stats.today}</p>
          </div>
          <FaExclamationTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-200" />
        </div>
      </div>
      
      <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl p-4 sm:p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-blue-100 text-sm sm:text-base">{t.remaining}</p>
            <p className="text-2xl sm:text-3xl font-bold">{stats.upcoming}</p>
          </div>
          <FaRegClock className="w-6 h-6 sm:w-8 sm:h-8 text-blue-200" />
        </div>
      </div>
    </motion.div>
  );
}

// مكون الرموز المتحركة للخلفية
function FloatingIcons() {
  const [windowSize, setWindowSize] = useState({ width: 1200, height: 800 });

  useEffect(() => {
    const updateWindowSize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    updateWindowSize();
    window.addEventListener('resize', updateWindowSize);
    return () => window.removeEventListener('resize', updateWindowSize);
  }, []);

  const icons = [
    { icon: FaCrown, delay: 0, duration: 3 },
    { icon: FaFlag, delay: 0.5, duration: 4 },
    { icon: FaMosque, delay: 1, duration: 3.5 },
    { icon: FaPrayingHands, delay: 1.5, duration: 4.5 },
    { icon: FaPalette, delay: 2, duration: 3.2 },
    { icon: FaRocket, delay: 2.5, duration: 4.2 },
    { icon: FaHeart, delay: 3, duration: 3.8 },
    { icon: FaStar, delay: 3.5, duration: 4.8 }
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {icons.map((item, index) => {
        const IconComponent = item.icon;
        return (
          <motion.div
            key={index}
            className="absolute text-white/10 dark:text-white/5"
            initial={{ 
              x: Math.random() * windowSize.width,
              y: windowSize.height + 100,
              rotate: 0,
              scale: 0.5
            }}
            animate={{
              y: -100,
              rotate: 360,
              scale: [0.5, 1, 0.5]
            }}
            transition={{
              duration: item.duration,
              delay: item.delay,
              repeat: Infinity,
              ease: "linear"
            }}
            style={{
              left: `${Math.random() * 100}%`,
              fontSize: `${Math.random() * 2 + 1}rem`
            }}
          >
            <IconComponent />
          </motion.div>
        );
      })}
    </div>
  );
}

// مكون التدرج اللوني المتحرك
function AnimatedGradient() {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-purple-600/20 via-pink-500/20 to-blue-600/20 dark:from-purple-800/30 dark:via-pink-700/30 dark:to-blue-800/30"
        animate={{
          background: [
            "linear-gradient(45deg, rgba(147, 51, 234, 0.2), rgba(236, 72, 153, 0.2), rgba(59, 130, 246, 0.2))",
            "linear-gradient(135deg, rgba(236, 72, 153, 0.2), rgba(59, 130, 246, 0.2), rgba(147, 51, 234, 0.2))",
            "linear-gradient(225deg, rgba(59, 130, 246, 0.2), rgba(147, 51, 234, 0.2), rgba(236, 72, 153, 0.2))",
            "linear-gradient(315deg, rgba(147, 51, 234, 0.2), rgba(236, 72, 153, 0.2), rgba(59, 130, 246, 0.2))"
          ]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-transparent to-transparent dark:from-gray-900/80" />
    </div>
  );
}

// مكون البقع المتحركة
function FloatingOrbs() {
  const [windowSize, setWindowSize] = useState({ width: 1200, height: 800 });

  useEffect(() => {
    const updateWindowSize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    updateWindowSize();
    window.addEventListener('resize', updateWindowSize);
    return () => window.removeEventListener('resize', updateWindowSize);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(6)].map((_, index) => (
        <motion.div
          key={index}
          className="absolute rounded-full bg-gradient-to-r from-purple-400/30 to-pink-400/30 dark:from-purple-600/20 dark:to-pink-600/20 blur-xl"
          initial={{
            x: Math.random() * windowSize.width,
            y: Math.random() * windowSize.height,
            scale: 0
          }}
          animate={{
            x: Math.random() * windowSize.width,
            y: Math.random() * windowSize.height,
            scale: [0, 1, 0]
          }}
          transition={{
            duration: Math.random() * 10 + 10,
            repeat: Infinity,
            delay: Math.random() * 5,
            ease: "easeInOut"
          }}
          style={{
            width: `${Math.random() * 200 + 100}px`,
            height: `${Math.random() * 200 + 100}px`
          }}
        />
      ))}
    </div>
  );
}

// دالة لحساب المواعيد المستقبلية المكررة
function getFutureRecurringDates(date: typeof saudiDates[0], count: number = 6): Date[] {
  const futureDates: Date[] = [];
  const now = new Date();
  
  // البطاقات المحددة التي تدعم المواعيد المكررة
  const recurringCards = ['governmentSalary', 'retiredSalary', 'housingSupport', 'socialSecurity', 'citizenAccount'];
  
  if (!recurringCards.includes(date.id)) {
    return futureDates;
  }
  
  // الحصول على التاريخ الحالي للموعد
  const currentDate = date.getNextDate();
  
  // إضافة المواعيد المستقبلية باستخدام الدوال المحسنة
  if (date.id === 'governmentSalary') {
    // رواتب الحكومة - 25 من كل شهر
    const monthlyDates = getFutureMonthlyDates(currentDate, 25, count);
    futureDates.push(...monthlyDates);
  } else if (date.id === 'retiredSalary') {
    // رواتب المتقاعدين - 3 من كل شهر
    const monthlyDates = getFutureMonthlyDates(currentDate, 3, count);
    futureDates.push(...monthlyDates);
  } else if (date.id === 'housingSupport') {
    // الدعم السكني - 15 من كل شهر
    const monthlyDates = getFutureMonthlyDates(currentDate, 15, count);
    futureDates.push(...monthlyDates);
  } else if (date.id === 'socialSecurity') {
    // الضمان الاجتماعي - 1 من كل شهر
    const monthlyDates = getFutureMonthlyDates(currentDate, 1, count);
    futureDates.push(...monthlyDates);
  } else if (date.id === 'citizenAccount') {
    // حساب المواطن - 10 من كل شهر
    const monthlyDates = getFutureMonthlyDates(currentDate, 10, count);
    futureDates.push(...monthlyDates);
  }
  
  // تصفية المواعيد لتشمل فقط المستقبلية
  return futureDates.filter(date => date > now);
}

// مكون عرض المواعيد المستقبلية
function FutureDatesModal({ 
  date, 
  isOpen, 
  onClose, 
  lang 
}: { 
  date: typeof saudiDates[0] | null, 
  isOpen: boolean, 
  onClose: () => void,
  lang: 'ar' | 'en'
}) {
  const t = translations[lang as keyof typeof translations];
  
  if (!date || !isOpen) return null;
  
  const futureDates = getFutureRecurringDates(date, 12);
  
  const formatDate = (date: Date, lang: 'ar' | 'en') => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    };
    
    return date.toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-US', options);
  };
  
  const calculateTimeRemaining = (targetDate: Date) => {
    const now = new Date();
    const difference = targetDate.getTime() - now.getTime();
    
    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, hasPassed: true };
    }
    
    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);
    
    return { days, hours, minutes, seconds, hasPassed: false };
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Enhanced Design */}
            <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 text-white p-6 relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16"></div>
                <div className="absolute bottom-0 right-0 w-24 h-24 bg-white rounded-full translate-x-12 translate-y-12"></div>
                <div className="absolute top-1/2 left-1/2 w-16 h-16 bg-white rounded-full -translate-x-8 -translate-y-8"></div>
              </div>
              
              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center space-x-4 rtl:space-x-reverse">
                  <motion.div 
                    className="p-3 bg-white/20 rounded-xl backdrop-blur-sm"
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <date.icon className="w-6 h-6" />
                  </motion.div>
                  <div>
                    <h2 className="text-xl font-bold">{date.title}</h2>
                    <p className="text-purple-100 text-sm">{date.description}</p>
                  </div>
                </div>
                <motion.button
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaTimes className="w-5 h-5" />
                </motion.button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="mb-6">
                <motion.h3 
                  className="text-lg font-semibold text-gray-900 dark:text-white mb-4"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  {lang === 'ar' ? 'المواعيد المستقبلية المكررة' : 'Future Recurring Dates'}
                </motion.h3>
                <motion.p 
                  className="text-gray-600 dark:text-gray-400 text-sm"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  {lang === 'ar' 
                    ? 'عرض المواعيد المستقبلية المكررة لهذا الحدث'
                    : 'Showing future recurring dates for this event'
                  }
                </motion.p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {futureDates.map((futureDate, index) => {
                  const timeRemaining = calculateTimeRemaining(futureDate);
                  const isNext = index === 0;
                  
                  return (
                    <motion.div
                      key={futureDate.getTime()}
                      initial={{ opacity: 0, y: 20, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: index * 0.1, type: "spring", stiffness: 300 }}
                      whileHover={{ 
                        scale: 1.02,
                        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                      }}
                      className={`
                        p-4 rounded-xl border-2 transition-all duration-300 relative overflow-hidden
                        ${isNext 
                          ? 'border-yellow-400 bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20' 
                          : 'border-gray-200 dark:border-gray-700 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50'
                        }
                        hover:shadow-lg
                      `}
                    >
                      {/* Background Pattern for Next Date */}
                      {isNext && (
                        <div className="absolute inset-0 opacity-5">
                          <div className="absolute top-0 right-0 w-16 h-16 bg-yellow-500 rounded-full translate-x-8 -translate-y-8"></div>
                          <div className="absolute bottom-0 left-0 w-12 h-12 bg-orange-500 rounded-full -translate-x-6 translate-y-6"></div>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between mb-3 relative z-10">
                        <div className="flex items-center space-x-2 rtl:space-x-reverse">
                          {isNext && (
                            <motion.div
                              animate={{ scale: [1, 1.2, 1], rotate: [0, 180, 360] }}
                              transition={{ duration: 3, repeat: Infinity }}
                              className="w-3 h-3 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full shadow-lg"
                            />
                          )}
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            {isNext 
                              ? (lang === 'ar' ? 'الموعد القادم' : 'Next Date')
                              : `${index + 1}`
                            }
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(futureDate, lang)}
                        </div>
                      </div>
                      
                      {!timeRemaining.hasPassed && (
                        <motion.div 
                          className="grid grid-cols-4 gap-2 text-center relative z-10"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: index * 0.1 + 0.5 }}
                        >
                          <motion.div 
                            className="bg-white dark:bg-gray-800 rounded-lg p-2 shadow-sm"
                            whileHover={{ scale: 1.05 }}
                          >
                            <div className="text-lg font-bold text-gray-900 dark:text-white">
                              {timeRemaining.days}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              {t.days}
                            </div>
                          </motion.div>
                          <motion.div 
                            className="bg-white dark:bg-gray-800 rounded-lg p-2 shadow-sm"
                            whileHover={{ scale: 1.05 }}
                          >
                            <div className="text-lg font-bold text-gray-900 dark:text-white">
                              {timeRemaining.hours}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              {t.hours}
                            </div>
                          </motion.div>
                          <motion.div 
                            className="bg-white dark:bg-gray-800 rounded-lg p-2 shadow-sm"
                            whileHover={{ scale: 1.05 }}
                          >
                            <div className="text-lg font-bold text-gray-900 dark:text-white">
                              {timeRemaining.minutes}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              {t.minutes}
                            </div>
                          </motion.div>
                          <motion.div 
                            className="bg-white dark:bg-gray-800 rounded-lg p-2 shadow-sm"
                            whileHover={{ scale: 1.05 }}
                          >
                            <div className="text-lg font-bold text-gray-900 dark:text-white">
                              {timeRemaining.seconds}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              {t.seconds}
                            </div>
                          </motion.div>
                        </motion.div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
              
              {futureDates.length === 0 && (
                <motion.div 
                  className="text-center py-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  >
                    <FaCalendarAlt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  </motion.div>
                  <p className="text-gray-600 dark:text-gray-400">
                    {lang === 'ar' 
                      ? 'لا توجد مواعيد مستقبلية متاحة'
                      : 'No future dates available'
                    }
                  </p>
                </motion.div>
              )}
              
              {/* Footer with Additional Info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="mt-8 p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-xl border border-purple-200 dark:border-purple-700"
              >
                <div className="flex items-center space-x-3 rtl:space-x-reverse mb-3">
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="w-2 h-2 bg-purple-500 rounded-full"
                  />
                  <h4 className="text-sm font-semibold text-purple-700 dark:text-purple-300">
                    {lang === 'ar' ? 'معلومات إضافية' : 'Additional Information'}
                  </h4>
                </div>
                <p className="text-xs text-purple-600 dark:text-purple-400">
                  {lang === 'ar' 
                    ? 'هذه المواعيد يتم تحديثها تلقائياً كل ثانية. المواعيد المكررة تعتمد على نمط التكرار الشهري أو السنوي حسب نوع الحدث.'
                    : 'These dates are updated automatically every second. Recurring dates depend on monthly or yearly patterns based on the event type.'
                  }
                </p>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// مكون عرض التاريخ مع التاريخ الهجري
function DateDisplay({ date, lang, dateId }: { 
  date: Date; 
  lang: 'ar' | 'en';
  dateId?: string;
}) {
  const [hijriDate, setHijriDate] = useState<{ year: number; month: number; day: number } | null>(null);
  
  // تحديد ما إذا كان هذا موعد إسلامي
  const islamicDates = ['eidAlFitr', 'eidAlAdha', 'ramadan', 'hajj'];
  const isIslamicDate = dateId && islamicDates.includes(dateId);
  
  // دالة مساعدة للحصول على اسم الشهر الهجري
  const getHijriMonthName = (month: number, lang: 'ar' | 'en'): string => {
    const hijriMonths = {
      ar: [
        'محرم', 'صفر', 'ربيع الأول', 'ربيع الآخر', 'جمادى الأولى', 'جمادى الآخرة',
        'رجب', 'شعبان', 'رمضان', 'شوال', 'ذو القعدة', 'ذو الحجة'
      ],
      en: [
        'Muharram', 'Safar', 'Rabi al-Awwal', 'Rabi al-Thani', 'Jumada al-Ula', 'Jumada al-Thani',
        'Rajab', 'Sha\'ban', 'Ramadan', 'Shawwal', 'Dhu al-Qadah', 'Dhu al-Hijjah'
      ]
    };
    
    return hijriMonths[lang][month - 1] || '';
  };
  
  useEffect(() => {
    if (isIslamicDate) {
      try {
        const hijri = gregorianToHijri(date);
        setHijriDate(hijri);
      } catch (error) {
        console.warn('خطأ في تحويل التاريخ الهجري:', error);
      }
    }
  }, [date, isIslamicDate]);
  
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long'
  };
  
  const gregorianText = date.toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-US', options);
  
  return (
    <div className="flex flex-col">
      <span className="text-xs sm:text-sm text-gray-800 dark:text-gray-200 font-bold">
        {gregorianText}
      </span>
      {isIslamicDate && hijriDate && (
        <span className="text-xs text-purple-600 dark:text-purple-400 font-medium mt-1">
          {lang === 'ar' 
            ? `${hijriDate.day} ${getHijriMonthName(hijriDate.month, lang)} ${hijriDate.year} هـ`
            : `${hijriDate.day} ${getHijriMonthName(hijriDate.month, lang)} ${hijriDate.year} AH`
          }
        </span>
      )}
    </div>
  );
}

// دالة تشخيص لفحص التواريخ المنتجة من الدوال الإسلامية
function debugIslamicDates() {
  console.log('=== تشخيص التواريخ الإسلامية ===');
  
  const islamicDateIds = ['ramadan', 'eidAlFitr', 'eidAlAdha', 'hajj'];
  
  islamicDateIds.forEach(id => {
    const dateItem = saudiDates.find(d => d.id === id);
    if (dateItem) {
      try {
        const targetDate = dateItem.getNextDate();
        const now = new Date();
        const difference = targetDate.getTime() - now.getTime();
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        
        console.log(`\n📅 ${dateItem.title}:`);
        console.log(`  التاريخ المستهدف: ${targetDate.toLocaleDateString('ar-SA')}`);
        console.log(`  التاريخ الحالي: ${now.toLocaleDateString('ar-SA')}`);
        console.log(`  الفرق بالأيام: ${days}`);
        console.log(`  في الماضي: ${difference < 0}`);
        console.log(`  Timestamp المستهدف: ${targetDate.getTime()}`);
        console.log(`  Timestamp الحالي: ${now.getTime()}`);
        console.log(`  صحة التاريخ: ${!isNaN(targetDate.getTime())}`);
        
        // فحص إضافي للتواريخ الهجرية
        try {
          const hijriDate = gregorianToHijri(targetDate);
          console.log(`  التاريخ الهجري: ${hijriDate.day}/${hijriDate.month}/${hijriDate.year} هـ`);
        } catch (error) {
          console.error(`  خطأ في تحويل التاريخ الهجري:`, error);
        }
        
      } catch (error) {
        console.error(`❌ خطأ في فحص ${dateItem.title}:`, error);
      }
    }
  });
  
  console.log('\n=== انتهى التشخيص ===');
}

export default function SaudiDates() {
  const { currentLanguage } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [showCompleted, setShowCompleted] = useState(false);
  const [selectedDate, setSelectedDate] = useState<typeof saudiDates[0] | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [performanceMode, setPerformanceMode] = useState<'LOW_END_DEVICE' | 'MEDIUM_DEVICE' | 'HIGH_END_DEVICE'>('MEDIUM_DEVICE');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // تحسين الأداء: استخدام useMemo للبيانات المفلترة
  const filteredDates = useMemo(() => {
    return activeCategory === 'all' 
      ? saudiDates 
      : saudiDates.filter(date => date.category === activeCategory);
  }, [activeCategory]);
  
  // تحسين الأداء: كشف نوع الجهاز عند التحميل
  useEffect(() => {
    const detectPerformance = () => {
      if (typeof window === 'undefined') return 'MEDIUM_DEVICE';
      
      // كشف سرعة الاتصال
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const isSlowConnection = connection && (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g');
      
      // كشف ذاكرة الجهاز
      const memory = performance.memory;
      const hasLowMemory = memory && memory.jsHeapSizeLimit && memory.jsHeapSizeLimit < 2147483648; // أقل من 2GB
      
      // كشف عدد النوى
      const cores = navigator.hardwareConcurrency || 2;
      const hasLowCores = cores <= 2;
      
      if (isSlowConnection || hasLowMemory || hasLowCores) {
        return 'LOW_END_DEVICE';
      } else if (cores >= 8 && !hasLowMemory) {
        return 'HIGH_END_DEVICE';
      } else {
        return 'MEDIUM_DEVICE';
      }
    };
    
    try {
      const detectedMode = detectPerformance();
      setPerformanceMode(detectedMode);
      
      // محاكاة تحميل البيانات
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    } catch (err) {
      setError('Failed to detect device performance');
      setIsLoading(false);
    }
  }, []);

  // اختبار التواريخ الإسلامية عند التحميل
  useEffect(() => {
    // اختبار التواريخ الفعلية لعام 2025
    test2025IslamicDates();
    
    // تشخيص إضافي
    debugIslamicDates();
  }, []);
  
  // تحسين الأداء: استخدام useCallback للدوال
  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);
  
  const handleCardClick = useCallback((date: typeof saudiDates[0]) => {
    setSelectedDate(date);
    setIsModalOpen(true);
  }, []);
  
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedDate(null);
  }, []);
  
  const handleRetry = useCallback(() => {
    setError(null);
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  }, []);
  
  // تحسين الأداء: تقليل عدد البطاقات المعروضة للأجهزة الضعيفة
  const visibleDates = useMemo(() => {
    const maxCards = performanceMode === 'LOW_END_DEVICE' ? 6 : 
                    performanceMode === 'MEDIUM_DEVICE' ? 9 : 12;
    return filteredDates.slice(0, maxCards);
  }, [filteredDates, performanceMode]);
  
  // تحسين الأداء: تقليل التأثيرات الحركية للأجهزة الضعيفة
  const animationDuration = useMemo(() => {
    return performanceMode === 'LOW_END_DEVICE' ? 0.3 : 
           performanceMode === 'MEDIUM_DEVICE' ? 0.5 : 0.8;
  }, [performanceMode]);
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <ErrorBoundary error={error} onRetry={handleRetry} />
      </div>
    );
  }
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">
            {translations[currentLanguage as keyof typeof translations].loading}
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="saudi-dates-app" dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header with Glass Effect - مطابق لحاسبة العمر */}
      <div className="saudi-header">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="saudi-btn"
              aria-label={currentLanguage === 'ar' ? "العودة إلى الصفحة الرئيسية" : "Back to Main Page"}
            >
              {currentLanguage === 'ar' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h17" />
                </svg>
              )}
            </Link>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCompleted(!showCompleted)}
                className="saudi-toggle-btn"
                aria-label={showCompleted ? "إخفاء المكتمل" : "إظهار المكتمل"}
              >
                {showCompleted ? translations[currentLanguage as keyof typeof translations].hideCompleted : translations[currentLanguage as keyof typeof translations].showCompleted}
              </button>
              <button
                onClick={handleRefresh}
                className="saudi-refresh-btn"
                aria-label={translations[currentLanguage as keyof typeof translations].refresh}
              >
                <FaClock className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Hero Section with Advanced Effects - محسن للأداء */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        {/* Background Effects - محسن للأداء */}
        {performanceMode !== 'LOW_END_DEVICE' && (
          <>
            <AnimatedGradient />
            <FloatingOrbs />
            <FloatingIcons />
          </>
        )}
        
        {/* Main Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-center">
          {/* Main Title with Advanced Styling - محسن للأداء */}
          <motion.div
            initial={{ opacity: 0, y: -50, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: animationDuration, ease: "easeOut" }}
            className="mb-8"
          >
            <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-6 relative">
              {/* Main Gradient Text */}
              <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent">
                {translations[currentLanguage as keyof typeof translations].title}
              </span>
              
              {/* Glow Effect - محسن للأداء */}
              {performanceMode !== 'LOW_END_DEVICE' && (
                <>
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent blur-lg opacity-30 scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent opacity-20 scale-110" />
                </>
              )}
            </h1>
            
            {/* Subtitle with Enhanced Styling */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: animationDuration * 0.75, delay: 0.2 }}
              className="text-lg sm:text-2xl md:text-3xl text-gray-700 dark:text-gray-300 max-w-4xl mx-auto mb-8 leading-relaxed"
            >
              {translations[currentLanguage as keyof typeof translations].subtitle}
            </motion.p>
            
            {/* Last Updated with Enhanced Design */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: animationDuration * 0.75, delay: 0.4 }}
              className="inline-block"
            >
              <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl px-6 py-4 shadow-2xl border border-white/20 dark:border-gray-700/20">
                <div className="flex items-center justify-center space-x-3 rtl:space-x-reverse">
                  <motion.div
                    animate={performanceMode === 'LOW_END_DEVICE' ? {} : { rotate: 360 }}
                    transition={performanceMode === 'LOW_END_DEVICE' ? {} : { duration: 2, repeat: Infinity, ease: "linear" }}
                    className="w-3 h-3 bg-gradient-to-r from-green-400 to-blue-500 rounded-full"
                  />
                  <span className="text-sm sm:text-base text-gray-600 dark:text-gray-400 font-medium">
                    {translations[currentLanguage as keyof typeof translations].lastUpdated}:
                  </span>
                  <span className="text-sm sm:text-base text-gray-800 dark:text-gray-200 font-semibold">
                    {new Date().toLocaleString(
                      currentLanguage === 'ar' ? 'ar-SA' : 'en-US',
                      {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        weekday: 'long'
                      }
                    )}
                  </span>
                </div>
              </div>
            </motion.div>
            
            {/* Decorative Elements - محسن للأداء */}
            {performanceMode !== 'LOW_END_DEVICE' && (
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: animationDuration, delay: 0.6 }}
                className="flex justify-center space-x-4 rtl:space-x-reverse mb-8"
              >
                {[...Array(3)].map((_, index) => (
                  <motion.div
                    key={index}
                    className="w-2 h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                    animate={{
                      scale: [1, 1.5, 1],
                      opacity: [0.5, 1, 0.5]
                    }}
                    transition={{
                      duration: 2,
                      delay: index * 0.2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                ))}
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>
      
      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Statistics */}
        <Statistics dates={saudiDates} lang={currentLanguage as 'ar' | 'en'} />
        
        {/* Filter Buttons */}
        <FilterButtons
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
          lang={currentLanguage as 'ar' | 'en'}
        />
        
        {/* Dates Grid - محسن للأداء */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: animationDuration * 0.6 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8"
          >
            {visibleDates.map((date) => (
              <DateCard
                key={date.id}
                date={date}
                lang={currentLanguage as 'ar' | 'en'}
                showCompleted={showCompleted}
                onCardClick={handleCardClick}
                performanceMode={performanceMode}
              />
            ))}
          </motion.div>
        </AnimatePresence>
        
        {/* Info Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: animationDuration }}
          className="mt-12 sm:mt-16 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center space-x-3 rtl:space-x-reverse mb-4 sm:mb-6">
            <div className="p-3 bg-blue-500 rounded-xl">
              <FaInfoCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
              {currentLanguage === 'ar' ? 'معلومات مهمة' : 'Important Information'}
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 text-gray-600 dark:text-gray-400">
            <div className="space-y-3">
              <p className="flex items-start space-x-2 rtl:space-x-reverse text-sm sm:text-base">
                <span className="text-blue-500 mt-1">•</span>
                <span>
                  {currentLanguage === 'ar' 
                    ? 'التواريخ المذكورة تقريبية وقد تتغير حسب الظروف الرسمية'
                    : 'The mentioned dates are approximate and may change according to official circumstances'
                  }
                </span>
              </p>
              <p className="flex items-start space-x-2 rtl:space-x-reverse text-sm sm:text-base">
                <span className="text-blue-500 mt-1">•</span>
                <span>
                  {currentLanguage === 'ar'
                    ? 'يتم تحديث التطبيق تلقائياً كل ثانية'
                    : 'The app updates automatically every second'
                  }
                </span>
              </p>
            </div>
            <div className="space-y-3">
              <p className="flex items-start space-x-2 rtl:space-x-reverse text-sm sm:text-base">
                <span className="text-blue-500 mt-1">•</span>
                <span>
                  {currentLanguage === 'ar'
                    ? 'للتواريخ الإسلامية، يتم استخدام التقويم الميلادي التقريبي'
                    : 'For Islamic dates, approximate Gregorian calendar is used'
                  }
                </span>
              </p>
              <p className="flex items-start space-x-2 rtl:space-x-reverse text-sm sm:text-base">
                <span className="text-blue-500 mt-1">•</span>
                <span>
                  {currentLanguage === 'ar'
                    ? 'يمكنك إظهار أو إخفاء المواعيد المكتملة'
                    : 'You can show or hide completed dates'
                  }
                </span>
              </p>
            </div>
          </div>
        </motion.div>
      </main>
      
      {/* Future Dates Modal */}
      <FutureDatesModal
        date={selectedDate}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        lang={currentLanguage as 'ar' | 'en'}
      />
    </div>
  );
} 